import { Grid } from '@material-ui/core';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  isResourceType,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
} from '@backstage/plugin-catalog';
import { EditResourceButton } from '../resourceEdit';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { EmptyState } from '@backstage/core-components';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';

import {
  EntityKubernetesContent,
  isKubernetesAvailable,
} from '@backstage/plugin-kubernetes';
import {
  EntityMlflowModelCard,
  isMlflowAvailable,
} from '@internal/plugin-mlflow';
import {
  EntityGitlabContent,
  EntityGitlabMergeRequestsTable,
  EntityGitlabPipelinesTable,
  isGitlabAvailable,
} from '@immobiliarelabs/backstage-plugin-gitlab';
import {
  EntityArgoCDOverviewCard,
  EntityArgoCDHistoryCard,
  isArgocdAvailable,
} from '@roadiehq/backstage-plugin-argo-cd';
import { MetricsContent, metricsClusterOf } from '../metrics/MetricsContent';
import {
  GitlabInstanceCard,
  GitlabStatsCard,
  GitlabSummaryProvider,
  GitlabUpdatesCard,
} from './gitlab';
import {
  ClusterAboutCard,
  ClusterDataProvider,
  ClusterLinksCard,
  ClusterNodesCard,
  ClusterKubectlCard,
} from './cluster';
import {
  KafkaDataProvider,
  KafkaNodesCard,
  KafkaTopicsCard,
  PostgresDatabasesCard,
  PostgresDataProvider,
  PostgresInstancesCard,
} from './datastore';
import {
  BROKER_TYPE,
  DATABASE_TYPE,
  MONGODB_TYPE,
  RABBITMQ_TYPE,
  VALKEY_TYPE,
  AIRFLOW_TYPE,
  DEFECTDOJO_TYPE,
  RUSTFS_TYPE,
  FEAST_TYPE,
  FEATURE_STORE_TYPE,
  JUPYTERHUB_TYPE,
  GITLAB_TYPE,
  KUBERNETES_CLUSTER_TYPE,
  SERVICE_TYPE,
} from '../../lib/viva';
import { jupyterhubEntityPage } from '../jupyterhub';
import { feastEntityPage } from '../feast';
import { airflowEntityPage } from '../airflow';
import { defectdojoEntityPage } from '../defectdojo';
import { rustfsEntityPage } from '../rustfs';
import { SecurityContent, securityProductOf } from '../security';

// Prometheus кластера: графики и алерты у кластеров, хранилищ и моделей (по привязке к кластеру)
const metricsTab = (
  <EntityLayout.Route
    path="/metrics"
    title="Метрики"
    if={e => Boolean(metricsClusterOf(e))}
  >
    <MetricsContent />
  </EntityLayout.Route>
);

// ArgoCD (Roadie): карточка со статусом Application у сущностей с аннотацией
// argocd/app-name или argocd/app-selector (кластеры из шаблона k8s-cluster, модели)
const argocdOverview = (
  <EntitySwitch>
    <EntitySwitch.Case if={e => Boolean(isArgocdAvailable(e))}>
      <Grid item md={12} xs={12}>
        <EntityArgoCDOverviewCard />
      </Grid>
    </EntitySwitch.Case>
  </EntitySwitch>
);
const argocdHistory = (
  <EntitySwitch>
    <EntitySwitch.Case if={e => Boolean(isArgocdAvailable(e))}>
      <Grid item md={12} xs={12}>
        <EntityArgoCDHistoryCard />
      </Grid>
    </EntitySwitch.Case>
  </EntitySwitch>
);

// DefectDojo кластера: находки проверок безопасности пайплайна (app-security -> app:defectdojo)
// у сущностей с проектом GitLab и кластером
const securityTab = (
  <EntityLayout.Route
    path="/security"
    title="Security"
    if={e => {
      const { cluster, product } = securityProductOf(e);
      return Boolean(cluster && product);
    }}
  >
    <SecurityContent />
  </EntityLayout.Route>
);

const gitlabTab = (
  <EntityLayout.Route path="/gitlab" title="GitLab" if={isGitlabAvailable}>
    <EntityGitlabContent />
  </EntityLayout.Route>
);

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

// Вкладка CI/CD: пайплайны и merge request'ы проекта GitLab (аннотация gitlab.com/project-slug
// из шаблонов app-service / ml-model). Раньше здесь была заглушка из стартового приложения.
const cicdContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isGitlabAvailable}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityGitlabPipelinesTable />
        </Grid>
        <Grid item xs={12}>
          <EntityGitlabMergeRequestsTable />
        </Grid>
      </Grid>
    </EntitySwitch.Case>
    <EntitySwitch.Case>
      <EmptyState
        title="CI/CD недоступен"
        missing="info"
        description="У сущности нет аннотации gitlab.com/project-slug — добавьте её в catalog-info.yaml (путь проекта в GitLab платформы), и здесь появятся пайплайны и merge request'ы."
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={6}>
      <EntityAboutCard />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard height={400} />
    </Grid>

    <Grid item md={4} xs={12}>
      <EntityLinksCard />
    </Grid>
    <Grid item md={8} xs={12}>
      <EntityHasSubcomponentsCard />
    </Grid>

    <EntitySwitch>
      <EntitySwitch.Case if={isMlflowAvailable}>
        <Grid item md={12} xs={12}>
          <EntityMlflowModelCard />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    {/* сервинг модели: Application <модель>-staging/-production из ApplicationSet кластера */}
    {argocdOverview}
  </Grid>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Обзор">
      {overviewContent}
    </EntityLayout.Route>
    {metricsTab}

    {gitlabTab}

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>
    {securityTab}

    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Зависимости">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Документация">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const websiteEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Обзор">
      {overviewContent}
    </EntityLayout.Route>

    {gitlabTab}

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Зависимости">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Документация">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Обзор">
      {overviewContent}
    </EntityLayout.Route>
    {metricsTab}

    {gitlabTab}

    <EntityLayout.Route path="/docs" title="Документация">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

// Ресурсы (Kubernetes-кластеры из шаблона k8s-cluster): карточка, граф связей и ссылки
// на ArgoCD/Grafana/MLflow/Vault; вкладка GitLab не нужна, у кластера нет своего проекта.
const clusterPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Обзор">
      {/* данные из Kubernetes API кластера (узлы, версия) — один запрос на обе карточки */}
      <ClusterDataProvider>
        <Grid container spacing={3} alignItems="stretch">
          {entityWarningContent}
          <EditResourceButton />
          {/* узлы по пулам (метка viva.io/pool) — Kubernetes API через GitLab Agent */}
          <Grid item md={12} xs={12}>
            <ClusterNodesCard />
          </Grid>
          <Grid item md={6} xs={12}>
            <ClusterAboutCard />
          </Grid>
          <Grid item md={6} xs={12}>
            <ClusterLinksCard />
          </Grid>
          {/* kubeconfig для kubectl под логином Keycloak (Pinniped) — бэкенд viva-kubectl */}
          <Grid item md={6} xs={12}>
            <ClusterKubectlCard />
          </Grid>
          {argocdOverview}
          {argocdHistory}
        </Grid>
      </ClusterDataProvider>
    </EntityLayout.Route>
    {metricsTab}
  </EntityLayout>
);

// Хранилища (Resource postgres-cluster / kafka-cluster): инстансы или узлы, базы и
// пользователи или топики — из API кластера через GitLab Agent; About, ссылки, ArgoCD, метрики
const postgresPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Обзор">
      <PostgresDataProvider>
        <Grid container spacing={3} alignItems="stretch">
          {entityWarningContent}
          <EditResourceButton />
          <Grid item md={12} xs={12}>
            <PostgresInstancesCard />
          </Grid>
          <Grid item md={12} xs={12}>
            <PostgresDatabasesCard />
          </Grid>
          <Grid item md={6} xs={12}>
            <EntityAboutCard />
          </Grid>
          <Grid item md={6} xs={12}>
            <EntityLinksCard />
          </Grid>
          {argocdOverview}
          {argocdHistory}
        </Grid>
      </PostgresDataProvider>
    </EntityLayout.Route>
    {metricsTab}
  </EntityLayout>
);

const kafkaPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Обзор">
      <KafkaDataProvider>
        <Grid container spacing={3} alignItems="stretch">
          {entityWarningContent}
          <EditResourceButton />
          <Grid item md={12} xs={12}>
            <KafkaNodesCard />
          </Grid>
          <Grid item md={12} xs={12}>
            <KafkaTopicsCard />
          </Grid>
          <Grid item md={6} xs={12}>
            <EntityAboutCard />
          </Grid>
          <Grid item md={6} xs={12}>
            <EntityLinksCard />
          </Grid>
          {argocdOverview}
          {argocdHistory}
        </Grid>
      </KafkaDataProvider>
    </EntityLayout.Route>
    {metricsTab}
  </EntityLayout>
);

// Хранилища на операторах MongoDB / RabbitMQ / Valkey: About (адрес в кластере), ссылки,
// состояние Application в ArgoCD, кнопка изменения; метрики — вкладка «Метрики»
const operatorDatastorePage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Обзор">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <EditResourceButton />
        <Grid item md={6} xs={12}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityLinksCard />
        </Grid>
        {argocdOverview}
        {argocdHistory}
      </Grid>
    </EntityLayout.Route>
    {metricsTab}
  </EntityLayout>
);

// GitLab платформы (Resource gitlab — добавлен платформой, не шаблоном): версия и KAS, статус
// обновлений, состав и состояние по метрикам кластера с компонентом gitlab-metrics, ссылки
const gitlabPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Обзор">
      <GitlabSummaryProvider>
        <Grid container spacing={3} alignItems="stretch">
          {entityWarningContent}
          <Grid item md={6} xs={12}>
            <GitlabInstanceCard />
          </Grid>
          <Grid item md={6} xs={12}>
            <GitlabUpdatesCard />
          </Grid>
          <Grid item md={6} xs={12}>
            <GitlabStatsCard />
          </Grid>
          <Grid item md={6} xs={12}>
            <EntityLinksCard />
          </Grid>
        </Grid>
      </GitlabSummaryProvider>
    </EntityLayout.Route>
    {metricsTab}
  </EntityLayout>
);

const resourcePage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isResourceType(KUBERNETES_CLUSTER_TYPE)}>
      {clusterPage}
    </EntitySwitch.Case>
    <EntitySwitch.Case if={isResourceType(DATABASE_TYPE)}>
      {postgresPage}
    </EntitySwitch.Case>
    <EntitySwitch.Case if={isResourceType(BROKER_TYPE)}>
      {kafkaPage}
    </EntitySwitch.Case>
    <EntitySwitch.Case
      if={isResourceType([MONGODB_TYPE, RABBITMQ_TYPE, VALKEY_TYPE])}
    >
      {operatorDatastorePage}
    </EntitySwitch.Case>
    <EntitySwitch.Case if={isResourceType(GITLAB_TYPE)}>
      {gitlabPage}
    </EntitySwitch.Case>
    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType(['service', SERVICE_TYPE])}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    {/* репозиторий признаков Feast: обзор + ArgoCD-приложение feast кластера (argocd/app-selector) */}
    <EntitySwitch.Case if={isComponentType(FEATURE_STORE_TYPE)}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    {/* JupyterHub кластера: серверы пользователей, приложение ArgoCD, поды namespace jupyter */}
    <EntitySwitch.Case if={isComponentType(JUPYTERHUB_TYPE)}>
      {jupyterhubEntityPage}
    </EntitySwitch.Case>

    {/* Feast кластера: материализация и online store из метрик, ArgoCD, репозитории признаков */}
    <EntitySwitch.Case if={isComponentType(FEAST_TYPE)}>
      {feastEntityPage}
    </EntitySwitch.Case>

    {/* Инстанс Airflow: адрес, репозиторий DAG'ов, ArgoCD, метрики statsd, CI/CD репозитория DAG'ов */}
    <EntitySwitch.Case if={isComponentType(AIRFLOW_TYPE)}>
      {airflowEntityPage}
    </EntitySwitch.Case>

    {/* DefectDojo кластера: адрес, кластеры-отправители отчётов, ArgoCD, поды */}
    <EntitySwitch.Case if={isComponentType(DEFECTDOJO_TYPE)}>
      {defectdojoEntityPage}
    </EntitySwitch.Case>

    {/* RustFS кластера на хостах: подключение к S3, бакеты, ArgoCD, поды, метрики */}
    <EntitySwitch.Case if={isComponentType(RUSTFS_TYPE)}>
      {rustfsEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Обзор">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Определение">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Обзор">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Обзор">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Обзор">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={8}>
          <EntityHasComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Схема">
      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="Схема системы"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Обзор">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />
    <EntitySwitch.Case if={isKind('resource')} children={resourcePage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
